import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { LayoutDashboard, Megaphone, Sparkles, Plug, BookOpen, Menu, X, LogOut, Inbox, Bot, CreditCard, Bell, MessageCircle, TrendingUp, Zap, Receipt, Check, Trash2, Camera, Phone, Video, Globe, BarChart2, ChevronRight, User, Shield, Gift, Wand2, Palette, Images, FileText, ListChecks, Users } from 'lucide-react'
import ProBadge from './ProBadge'
import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { useNotifications } from '../context/NotificationsContext'
import CopilotWidget from './CopilotWidget'
import { Lockup } from './Lockup'
import { ErrorBoundary } from './ErrorBoundary'

const PLAN_COPY_LIMITS = { free: 5, growth: 50, pro: 200, business: 999999 }
const PLAN_COLORS = {
  free: 'text-muted',
  growth: 'text-green-400',
  pro: 'text-aurora-violet-soft',
  business: 'text-yellow-400',
}

const ADMIN_EMAILS = new Set([
  'residenteskinksoul@gmail.com',
  'ceoskink@gmail.com',
  'appclixyads@gmail.com',
])

// NavItem se define fuera del Layout para evitar que se recree en cada render
// del padre (lo que causaría remount de los links y perderíamos estados internos
// como focus / hover de React Router).
function NavItem({ to, icon: Icon, label, premium }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `relative flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all min-w-0 ${
          isActive
            ? 'text-white border border-aurora-cyan/15'
            : 'text-muted hover:bg-white/[0.03] hover:text-white border border-transparent'
        }`
      }
      style={({ isActive }) =>
        isActive
          ? { background: 'linear-gradient(90deg, rgba(34,211,238,0.08), rgba(139,92,246,0.08))' }
          : undefined
      }
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span
              className="absolute -left-2 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r"
              style={{ background: 'linear-gradient(180deg, #22D3EE, #8B5CF6)' }}
            />
          )}
          <Icon size={18} className="flex-shrink-0" />
          <span className="flex-1 truncate">{label}</span>
          {premium && <ProBadge glow={false} />}
        </>
      )}
    </NavLink>
  )
}

const navItems = [
  { to: '/profile', icon: User, label: 'Mi perfil' },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/campaigns', icon: Megaphone, label: 'Campañas' },
  { to: '/inbox', icon: Inbox, label: 'Bandeja de entrada' },
  { to: '/automations', icon: Zap, label: 'Automatizaciones', premium: true },
  { to: '/crm', icon: Users, label: 'CRM' },
  { to: '/reports', icon: BarChart2, label: 'Informes' },
  { to: '/ai-agent', icon: Bot, label: 'Agente IA', premium: true },
  { to: '/image-generator', icon: Camera, label: 'Imágenes IA' },
  { to: '/connections', icon: Plug, label: 'Conexiones' },
  { to: '/referrals', icon: Gift, label: 'Invita y gana' },
  { divider: true, label: 'STUDIO' },
  { to: '/studio/setup', icon: Palette, label: 'Mi marca' },
  { to: '/studio/photos', icon: Images, label: 'Mis fotos' },
  { to: '/studio/create', icon: Wand2, label: 'Crear publicación' },
  { to: '/landings', icon: FileText, label: 'Landings' },
  { to: '/quizzes', icon: ListChecks, label: 'Quizzes' },
]

const adminNavItem = { to: '/admin', icon: Shield, label: 'Admin', adminOnly: true }

const typeConfig = {
  message: { icon: MessageCircle, color: 'text-aurora-violet-soft bg-aurora-violet/10' },
  campaign: { icon: TrendingUp, color: 'text-green-400 bg-green-400/10' },
  agent: { icon: Bot, color: 'text-blue-400 bg-blue-400/10' },
  billing: { icon: Receipt, color: 'text-yellow-400 bg-yellow-400/10' },
}

const platformIcon = {
  instagram: Camera,
  whatsapp: Phone,
  tiktok: Video,
  facebook: Globe,
}

function NotificationItem({ n, onRead, onDismiss, onNavigate }) {
  const { icon: Icon, color } = typeConfig[n.type] || typeConfig.message
  const PlatIcon = n.platform ? platformIcon[n.platform] : null

  return (
    <div
      className={`flex items-start gap-3 px-4 py-3.5 hover:bg-surface-2/50 transition-all cursor-pointer group relative ${!n.read ? 'bg-surface-2/20' : ''}`}
      onClick={() => { onRead(n.id); onNavigate(n.link) }}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 relative ${color.split(' ')[1]}`}>
        <Icon size={16} className={color.split(' ')[0]} />
        {PlatIcon && (
          <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-surface flex items-center justify-center">
            <PlatIcon size={9} className="text-muted" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${n.read ? 'text-muted' : 'text-white'}`}>{n.title}</p>
        <p className="text-dim text-xs truncate mt-0.5">{n.body}</p>
        <p className="text-dim text-xs mt-1">{n.time}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {!n.read && <div className="w-2 h-2 rounded-full bg-aurora-violet" />}
        <button
          onClick={e => { e.stopPropagation(); onDismiss(n.id) }}
          aria-label="Borrar notificación"
          className="p-1.5 rounded-lg text-dim hover:text-white hover:bg-white/10 md:opacity-0 md:group-hover:opacity-100 transition-all"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  )
}

function NotificationsPanel({ onClose, mobile = false }) {
  const { notifications, unread, markRead, markAllRead, dismiss, clearAll } = useNotifications()
  const navigate = useNavigate()

  const handleNavigate = (link) => {
    navigate(link)
    onClose()
  }

  const positioning = mobile
    ? 'w-full'
    : 'absolute right-0 top-full mt-2 w-80 z-50'

  return (
    <div className={`${positioning} bg-surface border border-line rounded-2xl shadow-2xl shadow-black/50 overflow-hidden`}>
      <div className="flex items-center justify-between px-4 py-3.5 border-b border-line">
        <div className="flex items-center gap-2">
          <span className="text-white font-semibold text-sm">Notificaciones</span>
          {unread > 0 && (
            <span className="aurora-gradient text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{unread}</span>
          )}
        </div>
        {unread > 0 && (
          <button onClick={markAllRead} className="text-xs text-aurora-violet-soft hover:text-white transition-all flex items-center gap-1">
            <Check size={11} />
            Marcar todas
          </button>
        )}
      </div>

      <div className="max-h-96 overflow-y-auto divide-y divide-line/60">
        {notifications.length === 0 ? (
          <div className="py-12 text-center">
            <Bell size={24} className="text-dim mx-auto mb-3" />
            <p className="text-dim text-sm">Sin notificaciones</p>
          </div>
        ) : (
          notifications.map(n => (
            <NotificationItem
              key={n.id}
              n={n}
              onRead={markRead}
              onDismiss={dismiss}
              onNavigate={handleNavigate}
            />
          ))
        )}
      </div>

      {notifications.length > 0 && (
        <div className="px-4 py-3 border-t border-line flex items-center justify-between gap-3">
          {unread > 0 ? (
            <button
              onClick={markAllRead}
              className="text-xs text-dim hover:text-white transition-all flex items-center gap-1.5"
            >
              <Check size={11} />
              Marcar leídas
            </button>
          ) : <span />}
          <button
            onClick={clearAll}
            className="text-xs text-red-400 hover:text-red-300 transition-all flex items-center gap-1.5"
          >
            <Trash2 size={11} />
            Borrar todas
          </button>
        </div>
      )}
    </div>
  )
}

export default function Layout() {
  const [open, setOpen] = useState(false)
  const [showNotifs, setShowNotifs] = useState(false)
  const { user, logout } = useAuth()
  const { unread } = useNotifications()
  const navigate = useNavigate()
  const notifRef = useRef(null)
  const notifMobileBtnRef = useRef(null)
  const notifMobilePanelRef = useRef(null)

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Cerrar panel al clicar fuera (considera desktop + mobile btn + mobile panel)
  useEffect(() => {
    if (!showNotifs) return
    const handler = (e) => {
      const t = e.target
      const inside =
        (notifRef.current && notifRef.current.contains(t)) ||
        (notifMobileBtnRef.current && notifMobileBtnRef.current.contains(t)) ||
        (notifMobilePanelRef.current && notifMobilePanelRef.current.contains(t))
      if (!inside) setShowNotifs(false)
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('touchstart', handler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('touchstart', handler)
    }
  }, [showNotifs])


  return (
    <div className="min-h-screen bg-ink text-white flex">

      {/* Sidebar desktop */}
      <aside className="hidden md:flex flex-col w-64 bg-surface border-r border-line p-6">
        <div className="flex items-center justify-between mb-10">
          <Lockup size="sm" kicker="PUBLICIDAD · IA" />

          {/* Campana notificaciones */}
          <div className="relative" ref={notifRef}>
            <button
              onMouseDown={e => e.stopPropagation()}
              onClick={() => setShowNotifs(v => !v)}
              aria-label="Notificaciones"
              className={`relative p-2 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-aurora-cyan/30 ${showNotifs ? 'aurora-gradient text-white' : 'text-muted hover:bg-surface-2 hover:text-white'}`}
            >
              <Bell size={18} />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
            {showNotifs && <NotificationsPanel onClose={() => setShowNotifs(false)} />}
          </div>
        </div>

        <nav className="flex flex-col gap-2 flex-1">
          {navItems.map(item =>
            item.divider
              ? <p key={item.label} className="text-[10px] font-mono-aurora font-semibold tracking-widest text-dim px-4 pt-4 pb-1">{item.label}</p>
              : <NavItem key={item.to} {...item} />
          )}
          {user?.email && ADMIN_EMAILS.has(user.email) && <NavItem {...adminNavItem} />}
        </nav>

        {/* Plan banner + User */}
        <div className="border-t border-line pt-4 mt-4 flex flex-col gap-2">

          {/* Banner plan */}
          {user?.plan !== 'business' && (
            <button
              onClick={() => navigate('/pricing')}
              className="w-full bg-aurora-violet/10 hover:bg-aurora-violet/20 border border-aurora-violet/20 rounded-xl p-3 text-left transition-all group"
            >
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-semibold uppercase tracking-wide ${PLAN_COLORS[user?.plan || 'free']}`}>
                  Plan {(user?.plan || 'free').charAt(0).toUpperCase() + (user?.plan || 'free').slice(1)}
                </span>
                <span className="text-xs text-aurora-violet-soft group-hover:text-white flex items-center gap-0.5 transition-all">
                  Mejorar <ChevronRight size={11} />
                </span>
              </div>
              <div className="h-1 bg-surface-2 rounded-full overflow-hidden mb-1.5">
                <div
                  className="h-full bg-aurora-violet rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, ((user?.copiesUsed || 0) / (PLAN_COPY_LIMITS[user?.plan || 'free'])) * 100)}%`
                  }}
                />
              </div>
              <p className="text-dim text-xs">
                {user?.copiesUsed || 0}/{PLAN_COPY_LIMITS[user?.plan || 'free'] >= 999999 ? '∞' : PLAN_COPY_LIMITS[user?.plan || 'free']} copies este mes
              </p>
            </button>
          )}

          {/* Avatar + nombre */}
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center gap-3 px-2 py-2 w-full hover:bg-surface-2 rounded-xl transition-all"
          >
            <div className="w-8 h-8 rounded-full aurora-gradient flex items-center justify-center text-sm font-bold flex-shrink-0">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <p className="text-white text-xs font-medium truncate">{user?.name}</p>
              <p className="text-dim text-xs truncate">{user?.email}</p>
            </div>
          </button>

          <div className="flex gap-1">
            <button
              onClick={() => navigate('/billing')}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs text-muted hover:bg-surface-2 hover:text-white transition-all"
            >
              <CreditCard size={13} />
              Facturación
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs text-muted hover:bg-surface-2 hover:text-white transition-all"
            >
              <LogOut size={13} />
              Salir
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-50 bg-surface border-b border-line px-4 py-3 flex items-center justify-between"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top))' }}
      >
        <Lockup size="sm" />
        <div className="flex items-center gap-2">
          {/* Campana móvil */}
          <div className="relative" ref={notifMobileBtnRef}>
            <button
              onClick={() => setShowNotifs(v => !v)}
              className="relative p-2 rounded-xl text-muted"
              aria-label="Notificaciones"
            >
              <Bell size={20} />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {unread > 9 ? '9+' : unread}
                </span>
              )}
            </button>
          </div>
          <button
            onClick={() => setOpen(!open)}
            aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
            className="text-muted p-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-aurora-cyan/30"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Panel notificaciones móvil */}
      {showNotifs && (
        <div
          className="md:hidden fixed left-3 right-3 z-50"
          ref={notifMobilePanelRef}
          style={{ top: 'calc(3.5rem + env(safe-area-inset-top))' }}
        >
          <NotificationsPanel onClose={() => setShowNotifs(false)} mobile />
        </div>
      )}

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-surface px-4 flex flex-col overflow-y-auto pb-[env(safe-area-inset-bottom)]"
          style={{ WebkitOverflowScrolling: 'touch', paddingTop: 'calc(4rem + env(safe-area-inset-top))' }}
        >
          <nav className="flex flex-col gap-2 flex-1">
            {navItems.map(item =>
              item.divider ? (
                <p key={item.label} className="text-[10px] font-mono-aurora font-semibold tracking-widest text-dim px-4 pt-4 pb-1">{item.label}</p>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `relative flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all min-w-0 ${
                      isActive
                        ? 'text-white border border-aurora-cyan/15'
                        : 'text-muted hover:bg-white/[0.03] hover:text-white border border-transparent'
                    }`
                  }
                  style={({ isActive }) =>
                    isActive
                      ? { background: 'linear-gradient(90deg, rgba(34,211,238,0.08), rgba(139,92,246,0.08))' }
                      : undefined
                  }
                >
                  <item.icon size={18} />
                  <span className="flex-1 truncate">{item.label}</span>
                  {item.premium && <ProBadge glow={false} />}
                </NavLink>
              )
            )}
            {user?.email && ADMIN_EMAILS.has(user.email) && (
              <NavLink
                to="/admin"
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `relative flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                    isActive
                      ? 'text-white border border-aurora-cyan/15'
                      : 'text-muted hover:bg-white/[0.03] hover:text-white border border-transparent'
                  }`
                }
                style={({ isActive }) =>
                  isActive
                    ? { background: 'linear-gradient(90deg, rgba(34,211,238,0.08), rgba(139,92,246,0.08))' }
                    : undefined
                }
              >
                <Shield size={18} />
                <span className="flex-1">Admin</span>
              </NavLink>
            )}
          </nav>

          <div className="border-t border-line pt-4 pb-6 flex flex-col gap-2">

            {/* Banner plan móvil */}
            {user?.plan !== 'business' && (
              <button
                onClick={() => { navigate('/pricing'); setOpen(false) }}
                className="w-full bg-aurora-violet/10 hover:bg-aurora-violet/20 border border-aurora-violet/20 rounded-xl p-3 text-left transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-semibold uppercase tracking-wide ${PLAN_COLORS[user?.plan || 'free']}`}>
                    Plan {(user?.plan || 'free').charAt(0).toUpperCase() + (user?.plan || 'free').slice(1)}
                  </span>
                  <span className="text-xs text-aurora-violet-soft group-hover:text-white flex items-center gap-0.5 transition-all">
                    Mejorar <ChevronRight size={11} />
                  </span>
                </div>
                <div className="h-1 bg-surface-2 rounded-full overflow-hidden mb-1.5">
                  <div
                    className="h-full bg-aurora-violet rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, ((user?.copiesUsed || 0) / (PLAN_COPY_LIMITS[user?.plan || 'free'])) * 100)}%`
                    }}
                  />
                </div>
                <p className="text-dim text-xs">
                  {user?.copiesUsed || 0}/{PLAN_COPY_LIMITS[user?.plan || 'free'] >= 999999 ? '∞' : PLAN_COPY_LIMITS[user?.plan || 'free']} copies este mes
                </p>
              </button>
            )}

            {/* Avatar + nombre */}
            <button
              onClick={() => { navigate('/profile'); setOpen(false) }}
              className="flex items-center gap-3 px-2 py-2 w-full hover:bg-surface-2 rounded-xl transition-all"
            >
              <div className="w-8 h-8 rounded-full aurora-gradient flex items-center justify-center text-sm font-bold flex-shrink-0">
                {user?.name?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-white text-xs font-medium truncate">{user?.name}</p>
                <p className="text-dim text-xs truncate">{user?.email}</p>
              </div>
            </button>

            <div className="flex gap-1">
              <button
                onClick={() => { navigate('/billing'); setOpen(false) }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs text-muted hover:bg-surface-2 hover:text-white transition-all"
              >
                <CreditCard size={13} />
                Facturación
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs text-muted hover:bg-surface-2 hover:text-white transition-all"
              >
                <LogOut size={13} />
                Salir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main
        className="flex-1 overflow-auto pt-[calc(3.5rem+env(safe-area-inset-top))] md:pt-0"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Widget Copiloto — fixed, visible en todas las páginas autenticadas */}
      <CopilotWidget />
    </div>
  )
}
